import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

/**
 * Profanity Filter Service
 * 
 * This service provides client-side validation for site tags to prevent profane or inappropriate words.
 * It loads the forbidden words list from the security configuration file.
 */
@Injectable({
	providedIn: 'root'
})
export class ProfanityFilterService {
	private forbiddenWords: string[] = [];
	private allowedExceptions: string[] = [];
	private configLoaded: boolean = false;

	constructor(private http: HttpClient) {
		this.loadConfig();
	}

	/**
	 * Load the profanity filter configuration from the JSON file
	 */
	private loadConfig(): void {
		this.http.get<any>('assets/security/forbidden_site_tag_words.json').subscribe(
			(config) => {
				this.forbiddenWords = config.forbiddenWords || [];
				this.allowedExceptions = config.allowedExceptions || [];
				this.configLoaded = true;
			},
			(error) => {
				console.error('Error loading profanity filter config:', error);
				this.forbiddenWords = [];
				this.allowedExceptions = [];
				this.configLoaded = true;
			}
		);
	}

	/**
	 * Validate a site tag against the profanity filter
	 * @param tagName - The tag name to validate
	 * @returns Object with validation result
	 */
	validateSiteTag(tagName: string): { valid: boolean; error: string | null; matchedWord: string | null } {
		// Check if tag name is provided
		if (!tagName || typeof tagName !== 'string') {
			return {
				valid: false,
				error: 'Tag name is required',
				matchedWord: null
			};
		}

		// Trim and convert to lowercase for comparison
		const lowerTagName = tagName.trim().toLowerCase();

		// Check if the tag is empty after trimming
		if (lowerTagName.length === 0) {
			return {
				valid: false,
				error: 'Tag name cannot be empty',
				matchedWord: null
			};
		}

		// First check if the tag is in the allowed exceptions list
		// This allows technical terms like 'dicky' to pass through
		for (const exception of this.allowedExceptions) {
			if (lowerTagName === exception.toLowerCase()) {
				return {
					valid: true,
					error: null,
					matchedWord: null
				};
			}
		}

		// Check for forbidden words (case-insensitive, partial matching)
		for (const word of this.forbiddenWords) {
			const lowerWord = word.toLowerCase();
			
			// Check if the forbidden word is contained in the tag name
			if (lowerTagName.includes(lowerWord)) {
				return {
					valid: false,
					error: 'Tag name contains inappropriate language',
					matchedWord: word
				};
			}
		}

		// Tag is valid
		return {
			valid: true,
			error: null,
			matchedWord: null
		};
	}

	/**
	 * Check if the configuration has been loaded
	 * @returns True if config is loaded
	 */
	isConfigLoaded(): boolean {
		return this.configLoaded;
	}

	/**
	 * Check if a word is in the allowed exceptions list
	 * @param word - The word to check
	 * @returns True if the word is an allowed exception
	 */
	isAllowedException(word: string): boolean {
		if (!word || typeof word !== 'string') {
			return false;
		}

		const lowerWord = word.toLowerCase();
		return this.allowedExceptions.some(exception => exception.toLowerCase() === lowerWord);
	}
}

